import { useCallback, useMemo, useSyncExternalStore } from "react";
import { api } from "../lib/api.js";
import {
  countUnread,
  countUnreadInSpace,
  newestMessage,
  type SpaceState,
} from "../lib/store.js";
import { syncEngine } from "../lib/sync.js";

/**
 * What is new since this person last looked (SPEC §4.5).
 *
 * Everything under here already existed and nothing called it: `countUnread`
 * had no callers, `api.markRead` had none either, and the route was waiting on
 * the server. So the app could not answer the one question two people ask it
 * every day — «мне написали?» — except by opening the general chat and then
 * every thread in turn.
 *
 * ## Why the cursor is kept here as well as on the server
 *
 * Marking read deliberately spends no revision: how far one person has scrolled
 * is not a change the other needs pushed at them, and burning a revision on it
 * would wake every device in the space for nothing. The cost of that decision
 * is paid here — with no revision there is no delta and no nudge, so this
 * device would go on showing the old count until something else happened to
 * pull. A cursor of our own, ahead of the server's, closes that gap.
 *
 * It is only ever moved *forwards*: two tabs open on two topics must not
 * undo each other, and neither must a delta that arrives carrying a cursor
 * older than the one we just set.
 */

/** The furthest each space has been read on this device, this session. */
const local = new Map<string, string>();

const listeners = new Set<() => void>();
let version = 0;

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

const snapshot = (): number => version;

function emit(): void {
  version += 1;
  for (const listener of listeners) listener();
}

/** The cursor to count from: the later of what the server knows and what we do. */
function cursorFor(state: SpaceState, spaceId: string, self: string): string | null {
  const server =
    state.members.find((member) => member.userId === self)?.lastReadMessageId ?? null;
  const ours = local.get(spaceId) ?? null;

  if (!server) return ours;
  if (!ours) return server;

  const a = state.messages[server];
  const b = state.messages[ours];
  // Whichever the mirror can actually place is worth more than one it cannot.
  if (!a) return ours;
  if (!b) return server;

  return b.createdAt > a.createdAt || (b.createdAt === a.createdAt && b.id > a.id)
    ? ours
    : server;
}

export interface Unread {
  /** Everything new in this space, wherever it is. */
  total: number;
  /** The same, for one conversation. */
  inTopic(topicId: string): number;
  /**
   * Say this person has read up to and including `messageId`.
   *
   * Safe to call on every render of an open conversation: it does nothing when
   * the cursor is already there or further on, so it makes no request and no
   * re-render.
   */
  markRead(messageId: string): void;
  /** Read everything in the space — what an open conversation at its end means. */
  markAllRead(): void;
}

export function useUnread(spaceId: string | null, self: string | null): Unread {
  const synced = useSyncExternalStore(syncEngine.subscribe, syncEngine.getSnapshot);
  const mine = useSyncExternalStore(subscribe, snapshot);

  const cursor = useMemo(
    () => (spaceId && self ? cursorFor(syncEngine.view(spaceId), spaceId, self) : null),
    // `synced` and `mine` are the dependencies that matter; the engine's view is
    // read fresh each time either moves.
    [spaceId, self, synced, mine],
  );

  const markRead = useCallback(
    (messageId: string) => {
      if (!spaceId || !self) return;

      const state = syncEngine.view(spaceId);
      const target = state.messages[messageId];
      if (!target) return;

      const standing = cursorFor(state, spaceId, self);
      const at = standing ? state.messages[standing] : undefined;

      // Already here or further on. Nothing to say, and nothing to redraw.
      if (
        at &&
        (at.createdAt > target.createdAt ||
          (at.createdAt === target.createdAt && at.id >= target.id))
      ) {
        return;
      }

      local.set(spaceId, messageId);
      emit();

      /*
       * Best effort, and deliberately not through the outbox.
       *
       * A read cursor is not a change to the space — nobody else sees it, and
       * queueing it would put "I scrolled" in a line behind somebody's message.
       * A lost one costs a count that is one visit stale.
       */
      void api.markRead(spaceId, messageId).catch(() => undefined);
    },
    [spaceId, self],
  );

  const markAllRead = useCallback(() => {
    if (!spaceId) return;
    const newest = newestMessage(syncEngine.view(spaceId));
    if (newest) markRead(newest.id);
  }, [spaceId, markRead]);

  return useMemo(() => {
    if (!spaceId || !self) {
      return { total: 0, inTopic: () => 0, markRead, markAllRead };
    }

    const state = syncEngine.view(spaceId);

    return {
      total: countUnreadInSpace(state, self, cursor),
      inTopic: (topicId: string) => countUnread(state, topicId, self, cursor),
      markRead,
      markAllRead,
    };
  }, [spaceId, self, cursor, markRead, markAllRead]);
}

/** Signing out takes this with it, like everything else this device knows. */
export function forgetReadCursors(): void {
  local.clear();
  emit();
}
